import React, { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import '../../vendor/stripe.js';

declare global {
  interface Window {
    Stripe: (apiKey: string) => any;
  }
}

interface StripeCardInputProps {
  setStripeData: (data: { token: string; brand: string; tokenPreferred: string }) => void;
  billingInfo: any;
}

const StripeCardInput: React.FC<StripeCardInputProps> = ({ setStripeData, billingInfo }) => {
  const cardElementRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  const [stripeValidated, setStripeValidated] = useState(false);
  const [cardState, setCardState] = useState();
  const [stripeState, setStripeState] = useState<any>();

  useEffect(() => {
    if (!cardState || !stripeValidated || !stripeState) return;

    (async () => {
      try {
        if (
          !billingInfo.firstName ||
          !billingInfo.lastName ||
          !billingInfo.email ||
          !billingInfo.address ||
          !billingInfo.phone ||
          !billingInfo.city ||
          !billingInfo.zipCode ||
          !billingInfo.country
        ) {
          return;
        }
        const result = await stripeState.createToken(cardState, {
          name: `${billingInfo.firstName} ${billingInfo.lastName}`,
          address_line1: `${billingInfo.address}`,
          address_city: billingInfo.city,
          address_state: billingInfo.state,
          address_zip: billingInfo.zipCode,
          address_country: billingInfo.country,
        });
        if (result.error) {
          setError(result.error.message);
        } else {
          const resultPreferred = await stripeState.createToken(cardState);
          let brand = result.token.card.brand;
          if (brand === "American Express") brand = "amex";
          if (brand === "Diners Club") brand = "diners";
          setStripeData({
            token: result.token.id,
            brand,
            tokenPreferred: resultPreferred.token.id,
          });
        }
      } catch (error) {
        Sentry.captureException(error);
        setError('An error occurred while creating the token.');
        console.error('Stripe createToken error:', error);
      }
    })();
  }, [stripeValidated, billingInfo, cardState, stripeState]);
  const style = {
    base: {
      color: '#32325d',
      lineHeight: '18px',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '20px',
      '::placeholder': { color: '#aab7c4' },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  };

  useEffect(() => {
    const stripe = window.Stripe(import.meta.env.VITE_STRIPE_API_KEY);
    const elements = stripe.elements();
    const card = elements.create('card', { style });
    card.mount(cardElementRef.current);

    card.addEventListener('change', async function (event: any) {
      if (cardElementRef.current) {
        if (event.error) {
          setError(event.error.message);
          cardElementRef.current.style.borderColor = '#dc3545';
        } else {
          setError('');
          cardElementRef.current.style.borderColor = '#a1a1a1';
        }
      }
      setStripeValidated(event.complete && !event.error);
    });
    setCardState(card);
    setStripeState(stripe);

    return () => {
      card.destroy();
    };
  }, []);

  return (
    <div className="form-group" style={{ minHeight: '95px' }}>
      <div
        id="card-element"
        ref={cardElementRef}
        style={{ border: '1px solid #bbb', borderRadius: '5px', padding: '25px 15px' }}
      ></div>
      {error && (
        <div id="card-errors" style={{ color: '#dc3545' }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default StripeCardInput;
